
// src/views/UsersManager.jsx
import React, { useState, useEffect } from 'react';
import { client } from '../utils/api';
import DataTable from '../components/DataTable';

const CONTROLLER = 'usersController.php';
const MEMBER_PROFILE_CONTROLLER = 'memberProfilesController.php';
const BASE_URL = process.env.REACT_APP_DB_BASE_URL || 'http://localhost/api';

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'createdAt', label: 'Created At' },
];

const initialFormState = {
  fullName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
};

const getDefaultMembershipDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  return {
    membershipFrom: `${year}-01-01`,
    membershipTo: `${year}-12-31`,
  };
};

const initialMemberProfileState = {
  userId: '',
  membershipFrom: '',
  membershipTo: '',
  status: '',
};

export default function UsersManager({ onSendEmail = () => {} }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [memberProfile, setMemberProfile] = useState(null);
  const [memberProfileForm, setMemberProfileForm] = useState(initialMemberProfileState);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await client.get(CONTROLLER);
      setUsers(res.users || []);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      let savedUser = null;

      if (editingId) {
        savedUser = await client.put(CONTROLLER, editingId, form);
      } else {
        savedUser = await client.post(CONTROLLER, form);
      }

      const userId = Number(editingId ?? savedUser?.user?.id ?? savedUser?.id);
      const hasMembershipValues = Boolean(
        memberProfileForm.status &&
          (memberProfileForm.membershipFrom || memberProfileForm.membershipTo)
      );

      if (userId && editingId && (memberProfile || hasMembershipValues)) {
        const payload = {
          userId,
          membershipFrom: memberProfileForm.membershipFrom || getDefaultMembershipDates().membershipFrom,
          membershipTo: memberProfileForm.membershipTo || getDefaultMembershipDates().membershipTo,
          status: memberProfileForm.status || '',
        };

        const memberResponse = await fetch(
          memberProfile
            ? `${BASE_URL}/${MEMBER_PROFILE_CONTROLLER}?userId=${userId}`
            : `${BASE_URL}/${MEMBER_PROFILE_CONTROLLER}`,
          {
            method: memberProfile ? 'PUT' : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );

        const memberResult = await memberResponse.json();
        if (!memberResponse.ok || !memberResult.ok) {
          throw new Error(memberResult.error || `HTTP error! status: ${memberResponse.status}`);
        }
      }

      setForm(initialFormState);
      setMemberProfileForm(initialMemberProfileState);
      setMemberProfile(null);
      setEditingId(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const loadMemberProfile = async (userId) => {
    if (!userId) {
      setMemberProfile(null);
      setMemberProfileForm({ ...initialMemberProfileState, userId: '' });
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/${MEMBER_PROFILE_CONTROLLER}?userId=${userId}`);
      const result = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setMemberProfile(null);
          setMemberProfileForm({
            userId: Number(userId),
            membershipFrom: getDefaultMembershipDates().membershipFrom,
            membershipTo: getDefaultMembershipDates().membershipTo,
            status: '',
          });
          return;
        }
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      const profile = result.memberProfile || null;
      setMemberProfile(profile);
      setMemberProfileForm({
        userId: Number(userId),
        membershipFrom: profile?.membershipFrom || getDefaultMembershipDates().membershipFrom,
        membershipTo: profile?.membershipTo || getDefaultMembershipDates().membershipTo,
        status: profile?.status || '',
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = async (user) => {
    setEditingId(user.id);
    setForm({
      fullName: user.fullName || '',
      email: user.email || '',
      phone: user.phone || '',
      addressLine1: user.addressLine1 || '',
      addressLine2: user.addressLine2 || '',
      city: user.city || '',
      state: user.state || '',
      postalCode: user.postalCode || '',
    });
    await loadMemberProfile(user.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await client.delete(CONTROLLER, id);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredUsers = users.filter((user) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    const searchableText = [
      user.fullName,
      user.email,
      user.phone,
      user.city,
      user.state,
      user.createdAt,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(query);
  });

  const toggleEmailSelection = (user) => {
    const email = String(user.email || '').trim();
    if (!email) return;

    setSelectedEmails((previous) => {
      if (previous.includes(email)) {
        return previous.filter((value) => value !== email);
      }
      return [...previous, email];
    });
  };

  const handleSendSelectedEmails = () => {
    if (!selectedEmails.length) {
      setError('Select at least one user to send an email.');
      return;
    }

    setError('');
    onSendEmail({ recipients: selectedEmails, subject: '' });
  };

  return (
    <div className="admin-view-wrapper">
      {/* 1. Header Row Area */}
      <div className="admin-view-header">
        <h2 className="admin-title">Users Management</h2>
        {error && <div className="admin-error">{error}</div>}
      </div>

      {/* 2. Form Area */}
      <form onSubmit={handleSubmit} className="admin-form-container grid-2-col">
        <div className="form-section-header" style={{ gridColumn: 'span 2' }}>
          <h4 className="form-section-title">Details</h4>
        </div>

        <div className="form-group">
          <label>Full Name *</label>
          <input
            type="text"
            placeholder="e.g. Alexandre Ivanov"
            required
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Email *</label>
          <input
            type="email"
            placeholder="e.g. alex@example.ch"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            placeholder="e.g. +41 21 000 00 00"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Address Line 1</label>
          <input
            type="text"
            placeholder="e.g. Rue de Bourg 12"
            value={form.addressLine1}
            onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Address Line 2</label>
          <input
            type="text"
            placeholder="e.g. Apt / Suite 4B"
            value={form.addressLine2}
            onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            placeholder="e.g. Lausanne"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>State / Canton</label>
          <input
            type="text"
            placeholder="e.g. Vaud"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Postal Code</label>
          <input
            type="text"
            placeholder="e.g. 1003"
            value={form.postalCode}
            onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
          />
        </div>

        {editingId && (
          <div className="form-section-divider" style={{ gridColumn: 'span 2' }}>
            <div>
              <h4 className="form-section-title">Membership</h4>

              <div className="admin-form-container membership-inline-form" style={{ padding: '16px', marginTop: '8px' }}>
                {memberProfile ? (
                  <div className="membership-inline-grid">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={memberProfileForm.status}
                        onChange={(e) => setMemberProfileForm({ ...memberProfileForm, status: e.target.value })}
                      >
                        <option value="">Not selected</option>
                        <option value="active">active</option>
                        <option value="inactive">inactive</option>
                        <option value="pending">pending</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Membership From</label>
                      <input
                        type="date"
                        value={memberProfileForm.membershipFrom}
                        onChange={(e) => setMemberProfileForm({ ...memberProfileForm, membershipFrom: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>Membership To</label>
                      <input
                        type="date"
                        value={memberProfileForm.membershipTo}
                        onChange={(e) => setMemberProfileForm({ ...memberProfileForm, membershipTo: e.target.value })}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="membership-inline-grid">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={memberProfileForm.status}
                        onChange={(e) => setMemberProfileForm({ ...memberProfileForm, status: e.target.value })}
                      >
                        <option value="">Not selected</option>
                        <option value="active">active</option>
                        <option value="inactive">inactive</option>
                        <option value="pending">pending</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Membership From</label>
                      <input
                        type="date"
                        value={memberProfileForm.membershipFrom}
                        onChange={(e) => setMemberProfileForm({ ...memberProfileForm, membershipFrom: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Membership To</label>
                      <input
                        type="date"
                        value={memberProfileForm.membershipTo}
                        onChange={(e) => setMemberProfileForm({ ...memberProfileForm, membershipTo: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div
          className="btn-actions-container"
          style={{ gridColumn: 'span 2', justifyContent: 'flex-end', marginTop: '8px' }}
        >
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(initialFormState);
                setMemberProfile(null);
                setMemberProfileForm(initialMemberProfileState);
              }}
              className="btn btn-cancel"
            >
              Cancel
            </button>
          )}
          <button type="submit" className="btn btn-submit-members">
            {editingId ? 'Update User' : 'Add User'}
          </button>
        </div>
      </form>

      {/* 3. Data Entry Table */}
      <div className="admin-table-section">
        <div className="admin-table-toolbar">
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label>Search users</label>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search by name, email, phone, city or date"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            type="button"
            className="btn btn-submit-professionals bulk-email-button"
            onClick={handleSendSelectedEmails}
            disabled={selectedEmails.length === 0}
          >
            Send Email to Selected
          </button>
        </div>
        <DataTable
          columns={columns}
          data={filteredUsers}
          onEdit={handleEdit}
          onDelete={handleDelete}
          emailSelection
          selectedEmails={selectedEmails}
          onToggleEmailSelection={toggleEmailSelection}
        />
      </div>
    </div>
  );
}